import { Component, computed, signal, viewChild, OnInit } from '@angular/core';
import { ModalComponent } from '@gallaghercp/core-components/modal';
import { BlockAbstractComponent } from '@gallaghercp/core-frontend';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

@Component({
    selector: 'core-block-contact-modal',
    templateUrl: './block-contact-modal.component.html',
    styleUrls: ['./block-contact-modal.component.scss'],
    standalone: false,
})
export class BlockContactModalComponent extends BlockAbstractComponent implements OnInit {
    modal = viewChild(ModalComponent);
    selectedContact = signal<any>(null);
    automatedContacts = signal<any[]>([]);

    modalId = computed(() => {
        const benefit = this.block.data?.title || this.block.data?.heading || 'benefit';
        const vendor = this.block.data?.subheading || 'vendor';
        return `contact-modal-${benefit}-${vendor}`.toLowerCase().replace(/\s+/g, '-');
    });

    get contactsViewType(): 'modal' | 'card' {
        return (this.configService.get('theme') as any)?.contactsViewType || 'modal';
    }

    get tileIcon(): string {
        return (
            (this.config?.automatedContacts as any)?.tileIcon ||
            (this.configService.get('automatedContacts') as any)?.tileIcon ||
            'modal-open-icon'
        );
    }

    get contacts(): any[] {
        if (Array.isArray(this.block.data?.contacts) && this.block.data.contacts.length > 0) {
            return this.block.data.contacts;
        }
        return this.automatedContacts();
    }

    openModal(contact: any) {
        this.selectedContact.set(contact);
        this.modal()?.open();
    }

    hasContactsArray(): boolean {
        return this.contacts.length > 0;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (Array.isArray(this.block.data?.contacts) && this.block.data.contacts.length > 0) {
            return;
        }

        const pageID = this.config.automatedContacts?.pageID;
        if (!pageID) return;

        const pageIds: string[] = String(pageID)
            .split(',')
            .map(id => id.trim())
            .filter(id => id.length > 0);

        if (pageIds.length > 0 && this.pages) {
            const pageRequests = pageIds.map(id =>
                this.pages!.getPage(id).pipe(
                    take(1),
                    catchError(() => of(null)),
                ),
            );

            this.subscriptions.push(
                forkJoin(pageRequests).subscribe((pages: (any | null)[]) => {
                    const currentPage = this.pages!.getCurrentPage();
                    if (currentPage) {
                        const findWidgets = (blocks: any[], identifier: string): any[] => {
                            let results: any[] = [];
                            for (const block of blocks) {
                                if (block.identifier === identifier) {
                                    results.push(block);
                                }
                                if (block.content) {
                                    results = results.concat(findWidgets(block.content, identifier));
                                }
                            }
                            return results;
                        };

                        const foundContacts: any[] = [];
                        for (const page of pages) {
                            if (!page) continue;
                            const widgets = findWidgets(page.widgets ?? [], 'CONTACT-TILES-AND-MODAL');
                            for (const widget of widgets) {
                                if (widget.data && Array.isArray(widget.data.contacts)) {
                                    for (const contact of widget.data.contacts) {
                                        if (contact) {
                                            let matchesPage = false;
                                            if (contact.pageId !== undefined && contact.pageId !== null) {
                                                const contactPageIds = String(contact.pageId)
                                                    .split(',')
                                                    .map(id => id.trim());
                                                matchesPage = contactPageIds.includes(String(currentPage.id));
                                            }
                                            if (matchesPage || String(contact.id) === String(currentPage.id)) {
                                                foundContacts.push(contact);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        this.automatedContacts.set(foundContacts);
                    }
                }),
            );
        }
    }
}
